import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

// ============================================
// TYPES
// ============================================

export type ArticleType = 'ARTICLE' | 'VIDEO' | 'FAQ';

export interface EducationCategory {
  id: string;
  name: string;
  slug: string;
  icon?: string;
  description?: string;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    articles: number;
  };
}

export interface EducationArticle {
  id: string;
  categoryId: string;
  type: ArticleType;
  title: string;
  slug: string;
  excerpt?: string;
  content: string;
  imageUrl?: string;
  videoUrl?: string;
  tags: string[];
  relatedDiseases: string[];
  order: number;
  authorName?: string;
  authorTitle?: string;
  viewCount: number;
  readTime?: number;
  isPublished: boolean;
  isFeatured: boolean;
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
  category?: EducationCategory;
}

export interface EducationFAQ {
  id: string;
  question: string;
  answer: string;
  categoryId?: string;
  tags: string[];
  order: number;
  viewCount: number;
  helpfulCount: number;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCategoryData {
  name: string;
  slug: string;
  icon?: string;
  description?: string;
  order?: number;
  isActive?: boolean;
}

export interface CreateArticleData {
  categoryId: string;
  type?: ArticleType;
  title: string;
  slug: string;
  excerpt?: string;
  content: string;
  imageUrl?: string;
  videoUrl?: string;
  tags?: string[];
  relatedDiseases?: string[];
  order?: number;
  authorName?: string;
  authorTitle?: string;
  readTime?: number;
  isPublished?: boolean;
  isFeatured?: boolean;
}

export interface CreateFAQData {
  question: string;
  answer: string;
  categoryId?: string;
  tags?: string[];
  order?: number;
  isPublished?: boolean;
}

// ============================================
// ADMIN API CALLS
// ============================================

// Categories
export const getAdminCategories = async (): Promise<EducationCategory[]> => {
  const token = localStorage.getItem('accessToken');
  const response = await axios.get(`${API_URL}/api/education/admin/categories`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const createCategory = async (data: CreateCategoryData): Promise<EducationCategory> => {
  const token = localStorage.getItem('accessToken');
  const response = await axios.post(`${API_URL}/api/education/admin/categories`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const updateCategory = async (id: string, data: Partial<CreateCategoryData>): Promise<EducationCategory> => {
  const token = localStorage.getItem('accessToken');
  const response = await axios.put(`${API_URL}/api/education/admin/categories/${id}`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const deleteCategory = async (id: string): Promise<void> => {
  const token = localStorage.getItem('accessToken');
  await axios.delete(`${API_URL}/api/education/admin/categories/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
};

// Articles
export const getAdminArticles = async (filters?: {
  categoryId?: string;
  type?: ArticleType;
  isPublished?: boolean;
}): Promise<EducationArticle[]> => {
  const token = localStorage.getItem('accessToken');
  const params = new URLSearchParams();
  if (filters?.categoryId) params.append('categoryId', filters.categoryId);
  if (filters?.type) params.append('type', filters.type);
  if (filters?.isPublished !== undefined) params.append('isPublished', String(filters.isPublished));

  const response = await axios.get(`${API_URL}/api/education/admin/articles?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const getAdminArticle = async (id: string): Promise<EducationArticle> => {
  const token = localStorage.getItem('accessToken');
  const response = await axios.get(`${API_URL}/api/education/admin/articles/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const createArticle = async (data: CreateArticleData): Promise<EducationArticle> => {
  const token = localStorage.getItem('accessToken');
  const response = await axios.post(`${API_URL}/api/education/admin/articles`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const updateArticle = async (id: string, data: Partial<CreateArticleData>): Promise<EducationArticle> => {
  const token = localStorage.getItem('accessToken');
  const response = await axios.put(`${API_URL}/api/education/admin/articles/${id}`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const deleteArticle = async (id: string): Promise<void> => {
  const token = localStorage.getItem('accessToken');
  await axios.delete(`${API_URL}/api/education/admin/articles/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
};

export const toggleArticlePublish = async (id: string): Promise<EducationArticle> => {
  const token = localStorage.getItem('accessToken');
  const response = await axios.post(`${API_URL}/api/education/admin/articles/${id}/publish`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const uploadImage = async (file: File): Promise<{ imageUrl: string }> => {
  const token = localStorage.getItem('accessToken');
  const formData = new FormData();
  formData.append('image', file);

  const response = await axios.post(`${API_URL}/api/education/admin/upload-image`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

// FAQs
export const getAdminFAQs = async (): Promise<EducationFAQ[]> => {
  const token = localStorage.getItem('accessToken');
  const response = await axios.get(`${API_URL}/api/education/admin/faqs`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const createFAQ = async (data: CreateFAQData): Promise<EducationFAQ> => {
  const token = localStorage.getItem('accessToken');
  const response = await axios.post(`${API_URL}/api/education/admin/faqs`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const updateFAQ = async (id: string, data: Partial<CreateFAQData>): Promise<EducationFAQ> => {
  const token = localStorage.getItem('accessToken');
  const response = await axios.put(`${API_URL}/api/education/admin/faqs/${id}`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const deleteFAQ = async (id: string): Promise<void> => {
  const token = localStorage.getItem('accessToken');
  await axios.delete(`${API_URL}/api/education/admin/faqs/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
};

// ============================================
// PUBLIC API CALLS (for users)
// ============================================

export const getCategories = async (): Promise<EducationCategory[]> => {
  const response = await axios.get(`${API_URL}/api/education/categories`);
  return response.data;
};

export const getCategoryArticles = async (
  slug: string,
  type?: ArticleType
): Promise<{ category: EducationCategory; articles: EducationArticle[] }> => {
  const params = type ? `?type=${type}` : '';
  const response = await axios.get(`${API_URL}/api/education/categories/${slug}/articles${params}`);
  return response.data;
};

export const getArticle = async (slug: string): Promise<EducationArticle> => {
  const response = await axios.get(`${API_URL}/api/education/articles/${slug}`);
  return response.data;
};

export const getAllArticles = async (): Promise<EducationArticle[]> => {
  const response = await axios.get(`${API_URL}/api/education/articles`);
  return response.data;
};

export const getFeaturedArticles = async (): Promise<EducationArticle[]> => {
  const response = await axios.get(`${API_URL}/api/education/featured`);
  return response.data;
};

export const searchArticles = async (params: {
  q: string;
  category?: string;
  tags?: string;
}): Promise<EducationArticle[]> => {
  const searchParams = new URLSearchParams();
  searchParams.append('q', params.q);
  if (params.category) searchParams.append('category', params.category);
  if (params.tags) searchParams.append('tags', params.tags);

  const response = await axios.get(`${API_URL}/api/education/search?${searchParams.toString()}`);
  return response.data;
};

export const getFAQs = async (categoryId?: string): Promise<EducationFAQ[]> => {
  const params = categoryId ? `?categoryId=${categoryId}` : '';
  const response = await axios.get(`${API_URL}/api/education/faqs${params}`);
  return response.data;
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

export const getAbsoluteImageUrl = (imageUrl: string | undefined | null): string | undefined => {
  if (!imageUrl) return undefined;
  // If already absolute (http/https), return as-is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  // Convert relative path to absolute using API_URL
  return `${API_URL}${imageUrl}`;
};

export const generateSlug = (text: string): string => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

export const estimateReadTime = (content: string): number => {
  const wordsPerMinute = 200;
  const wordCount = content.trim().split(/\s+/).length;
  return Math.ceil(wordCount / wordsPerMinute);
};

export const extractYouTubeId = (url: string): string | null => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};
